import { useEffect, useMemo, useState } from 'react';
import Header from '../../shared/layout/Header';
import developersApi from '../../api/services/developerService';
import './DevelopersPanel.css';

const label = {
  VERIFIED: 'تایید شده',
  UNVERIFIED: 'تایید نشده',
  UNDER_REVIEW: 'در حال بررسی',
  BASIC: 'پایه',
  MEDIUM: 'متوسط',
  PREMIUM: 'حرفه ای',
  NONE: 'بدون سطح',
  PORTFOLIO: 'سابقه کاری',
  PLATFORM: 'پروژه فعال',
  DRAFT: 'پیش نویس',
  PLANNING: 'برنامه ریزی',
  CONSTRUCTION: 'در حال ساخت',
  COMPLETED: 'تکمیل شده'
};

function Badge({ value }) {
  return <span className="dev-badge">{label[value] || value || 'نامشخص'}</span>;
}

function DeveloperProfiles() {
  const [developers, setDevelopers] = useState([]);
  const [ratings, setRatings] = useState([]);
  const [activeId, setActiveId] = useState('');
  const [portfolioProjects, setPortfolioProjects] = useState([]);
  const [activeProjects, setActiveProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectBundle, setProjectBundle] = useState({ sections: [], templates: [], units: [], schedules: [], activities: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeDeveloper = useMemo(
    () => developers.find((item) => item.id === activeId) || developers[0],
    [developers, activeId]
  );

  const ratingMap = useMemo(() => {
    const map = {};
    ratings.forEach((rating) => { map[rating.developer] = rating; });
    return map;
  }, [ratings]);

  useEffect(() => {
    loadDevelopers();
  }, []);

  useEffect(() => {
    if (activeDeveloper?.id) {
      loadDeveloperProjects(activeDeveloper.id);
    }
  }, [activeDeveloper?.id]);

  useEffect(() => {
    if (selectedProject?.id) {
      loadProjectDetails(selectedProject.id);
    }
  }, [selectedProject?.id]);

  const loadDevelopers = async () => {
    setLoading(true);
    setError('');
    try {
      const [visibleDevelopers, developerRatings] = await Promise.all([
        developersApi.listVisibleFirms(),
        developersApi.listRatings().catch(() => [])
      ]);
      setDevelopers(visibleDevelopers);
      setRatings(developerRatings);
      setActiveId(visibleDevelopers[0]?.id || '');
    } catch (err) {
      setError('در حال حاضر امکان دریافت عمومی پروفایل سازندگان از API موجود نیست.');
    } finally {
      setLoading(false);
    }
  };

  const loadDeveloperProjects = async (developerId) => {
    const [portfolio, active] = await Promise.all([
      developersApi.listPortfolioProjects(developerId).catch(() => []),
      developersApi.listPlatformProjects(developerId).catch(() => [])
    ]);
    setPortfolioProjects(portfolio);
    setActiveProjects(active);
    setSelectedProject(active[0] || portfolio[0] || null);
  };

  const loadProjectDetails = async (projectId) => {
    const [sections, templates, units, schedules, activities] = await Promise.all([
      developersApi.listSections(projectId).catch(() => []),
      developersApi.listUnitTemplates(projectId).catch(() => []),
      developersApi.listUnits(projectId).catch(() => []),
      developersApi.listScheduleVersions(projectId).catch(() => []),
      developersApi.listActivities(projectId).catch(() => [])
    ]);
    setProjectBundle({ sections, templates, units, schedules, activities });
  };

  return (
    <div className="dev-page" dir="rtl">
      <Header />
      <main className="dev-shell">
        <section className="dev-hero compact">
          <div>
            <p className="dev-kicker">پروفایل سازندگان</p>
            <h1>اعتبار، امتیاز و پروژه های سازندگان</h1>
            <p>کارت هر سازنده خلاصه اطلاعات و امتیاز را نشان می دهد. با انتخاب کارت، پروژه های سابق و فعال به صورت دسته بندی شده نمایش داده می شوند.</p>
          </div>
        </section>

        {error && <div className="dev-alert error">{error}</div>}
        {loading && <div className="dev-alert success">در حال بارگذاری...</div>}

        <section className="dev-profile-layout">
          <div className="dev-profile-cards">
            {developers.map((developer) => {
              const rating = ratingMap[developer.id];
              return (
                <button
                  key={developer.id}
                  className={`dev-profile-card ${activeDeveloper?.id === developer.id ? 'active' : ''}`}
                  onClick={() => setActiveId(developer.id)}
                >
                  <div className="dev-profile-avatar">{(developer.display_name || developer.legal_name || 'س').charAt(0)}</div>
                  <div>
                    <strong>{developer.display_name || developer.legal_name}</strong>
                    <span>{developer.legal_name}</span>
                  </div>
                  <div className="dev-card-meta">
                    <Badge value={developer.verification_status} />
                    <Badge value={developer.verification_level} />
                    <span>{rating ? `${rating.score} امتیاز از ${rating.response_count} پاسخ` : 'بدون امتیاز ثبت شده'}</span>
                  </div>
                </button>
              );
            })}
            {!developers.length && !loading && (
              <div className="dev-panel">
                <p className="dev-muted">هیچ پروفایل قابل نمایشی از API فعلی دریافت نشد.</p>
              </div>
            )}
          </div>

          <div className="dev-profile-detail">
            <div className="dev-panel">
              <div className="dev-panel-head">
                <div>
                  <h2>{activeDeveloper?.display_name || 'سازنده انتخاب نشده'}</h2>
                  <p>{activeDeveloper?.description || 'توضیحی ثبت نشده است.'}</p>
                </div>
                {activeDeveloper && <Badge value={activeDeveloper.verification_status} />}
              </div>
              <div className="dev-kpis">
                <div><strong>{portfolioProjects.length}</strong><span>پروژه سابق</span></div>
                <div><strong>{activeProjects.length}</strong><span>پروژه فعال</span></div>
                <div><strong>{ratingMap[activeDeveloper?.id]?.score || '-'}</strong><span>امتیاز</span></div>
                <div><strong>{activeDeveloper?.listing_status || '-'}</strong><span>وضعیت عرضه</span></div>
              </div>
            </div>

            <div className="dev-grid detail-grid">
              <div className="dev-panel">
                <h2>پروژه های فعال</h2>
                <div className="dev-list">
                  {activeProjects.map((project) => (
                    <button key={project.id} className="dev-list-row" onClick={() => setSelectedProject(project)}>
                      <span>{project.name}</span>
                      <Badge value={project.stage} />
                    </button>
                  ))}
                </div>
              </div>

              <div className="dev-panel">
                <h2>پروژه های سابق</h2>
                <div className="dev-list">
                  {portfolioProjects.map((project) => (
                    <button key={project.id} className="dev-list-row" onClick={() => setSelectedProject(project)}>
                      <span>{project.name}</span>
                      <small>{project.completed_at || project.expected_completion_at || 'بدون تاریخ'}</small>
                    </button>
                  ))}
                </div>
              </div>

              <div className="dev-panel wide">
                <h2>جزئیات پروژه</h2>
                {selectedProject ? (
                  <>
                    <div className="dev-project-title">
                      <strong>{selectedProject.name}</strong>
                      <Badge value={selectedProject.record_kind} />
                      <Badge value={selectedProject.stage} />
                    </div>
                    <div className="dev-categories">
                      <section>
                        <h3>اطلاعات عمومی</h3>
                        <p>{selectedProject.description || 'بدون توضیح'}</p>
                        <small>{selectedProject.address}</small>
                      </section>
                      <section>
                        <h3>زمان بندی</h3>
                        <p>شروع: {selectedProject.construction_started_at || '-'} | تحویل: {selectedProject.expected_completion_at || selectedProject.completed_at || '-'}</p>
                      </section>
                      <section>
                        <h3>ساختار و واحدها</h3>
                        <p>{projectBundle.sections.length} بخش، {projectBundle.templates.length} تیپ واحد، {projectBundle.units.length} واحد</p>
                      </section>
                      <section>
                        <h3>پیشرفت</h3>
                        <div className="dev-progress"><span style={{ width: `${Number(selectedProject.current_progress || 0)}%` }} /></div>
                        <small>{selectedProject.current_progress || 0}% ثبت شده</small>
                      </section>
                      <section>
                        <h3>برنامه ساخت</h3>
                        <p>{projectBundle.schedules.length} نسخه برنامه، {projectBundle.activities.length} فعالیت</p>
                      </section>
                      <section>
                        <h3>مدارک و مراجع</h3>
                        <p>{selectedProject.documents?.length || 0} مدرک، {selectedProject.customer_references?.length || 0} مرجع مشتری</p>
                      </section>
                    </div>
                  </>
                ) : (
                  <p className="dev-muted">برای مشاهده جزئیات، یک پروژه را انتخاب کنید.</p>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default DeveloperProfiles;
